// ─────────────────────────────────────────────────────────────────────────────
// text_input.ts — Canvas-based text input with keyboard support
//
// Opens over a text block, accepts keyboard input, renders a blinking cursor,
// and returns the edited content when closed.  All rendering is Canvas 2D.
//
// The implementation uses a hidden <textarea> to capture keyboard events
// reliably (IME, clipboard, mobile keyboards) while rendering the visual
// representation entirely on the canvas.
// ─────────────────────────────────────────────────────────────────────────────

export interface TextInputConfig {
  blockId: string;
  pageId: string;
  /** Bounding box in page-local coords. */
  x: number;
  y: number;
  width: number;
  height: number;
  content: string;
  fontSize: number;
  onCommit: (blockId: string, pageId: string, content: string) => void;
}

export class TextInputLayer {
  private _cfg: TextInputConfig | null = null;
  private _textarea: HTMLTextAreaElement | null = null;
  private _cursor = 0;
  private _selStart = 0;
  private _selEnd = 0;
  private _scrollY = 0;
  private _blinkOn = true;
  private _blinkTimer = 0;

  get active(): boolean { return this._cfg !== null; }
  get blockId(): string | null { return this._cfg?.blockId ?? null; }

  // ── Lifecycle ──────────────────────────────────────────────────────────

  open(cfg: TextInputConfig): void {
    this.close();
    this._cfg = cfg;
    this._cursor = cfg.content.length;
    this._selStart = this._selEnd = this._cursor;
    this._scrollY = 0;

    // Create hidden textarea for keyboard capture
    const ta = document.createElement('textarea');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '-9999px';
    ta.style.opacity = '0';
    ta.style.width = '1px';
    ta.style.height = '1px';
    ta.value = cfg.content;
    ta.setSelectionRange(this._cursor, this._cursor);
    document.body.appendChild(ta);
    ta.focus();

    ta.addEventListener('input', this._onInput);
    ta.addEventListener('keydown', this._onKeyDown);
    ta.addEventListener('select', this._onSelect);

    this._textarea = ta;
    this._blinkOn = true;
    this._blinkTimer = window.setInterval(() => {
      this._blinkOn = !this._blinkOn;
    }, 530);
  }

  close(): void {
    if (!this._cfg) return;
    const cfg = this._cfg;
    const content = this._textarea?.value ?? cfg.content;
    this._cleanup();
    cfg.onCommit(cfg.blockId, cfg.pageId, content);
  }

  /** Dismiss without committing changes. */
  cancel(): void {
    this._cleanup();
  }

  private _cleanup(): void {
    if (this._textarea) {
      this._textarea.removeEventListener('input', this._onInput);
      this._textarea.removeEventListener('keydown', this._onKeyDown);
      this._textarea.removeEventListener('select', this._onSelect);
      this._textarea.remove();
      this._textarea = null;
    }
    if (this._blinkTimer) { clearInterval(this._blinkTimer); this._blinkTimer = 0; }
    this._cfg = null;
  }

  /** Re-focus the hidden textarea (call after canvas pointer interactions). */
  refocus(): void {
    this._textarea?.focus();
  }

  // ── Event handlers ─────────────────────────────────────────────────────

  private _onInput = (): void => {
    if (!this._textarea) return;
    this._syncFromTextarea();
    this._resetBlink();
  };

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    // Let the textarea handle everything else natively
    // Sync after a microtask to pick up changes
    requestAnimationFrame(() => this._syncFromTextarea());
    this._resetBlink();
  };

  private _onSelect = (): void => {
    this._syncFromTextarea();
  };

  private _syncFromTextarea(): void {
    if (!this._textarea) return;
    this._cursor = this._textarea.selectionEnd ?? 0;
    this._selStart = this._textarea.selectionStart ?? this._cursor;
    this._selEnd = this._textarea.selectionEnd ?? this._cursor;
  }

  private _resetBlink(): void {
    this._blinkOn = true;
  }

  // ── Hit test ───────────────────────────────────────────────────────────

  hitTest(x: number, y: number): boolean {
    if (!this._cfg) return false;
    const c = this._cfg;
    const pad = 8;
    return x >= c.x - pad && x <= c.x + c.width + pad &&
           y >= c.y - pad && y <= c.y + c.height + pad;
  }

  // ── Render ─────────────────────────────────────────────────────────────

  draw(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
    if (!this._cfg) return;
    const c = this._cfg;
    const content = this._textarea?.value ?? c.content;
    const x = ox + c.x;
    const y = oy + c.y;
    const w = c.width;
    const h = c.height;
    const fs = c.fontSize;
    const lineH = fs + 4;
    const padX = 6;
    const padY = 4;

    ctx.save();

    // Very faint background — mostly transparent so page shows through
    ctx.fillStyle = 'rgba(238, 220, 176, 0.12)';
    ctx.fillRect(x, y, w, h);

    // Subtle border
    ctx.strokeStyle = 'rgba(90, 56, 32, 0.22)';
    ctx.lineWidth = 0.8;
    ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

    // Clip to input area
    ctx.beginPath();
    ctx.rect(x + 1, y + 1, w - 2, h - 2);
    ctx.clip();

    // Text rendering
    ctx.font = `${fs}px Georgia, serif`;
    ctx.fillStyle = 'rgba(50, 35, 15, 0.88)';
    ctx.textBaseline = 'top';

    // Wrap text into lines
    const lines = this._wrapLines(ctx, content, w - padX * 2);
    const textY = y + padY - this._scrollY;

    // Draw selection highlight
    if (this._selStart !== this._selEnd) {
      ctx.fillStyle = 'rgba(160, 120, 60, 0.25)';
      let charIdx = 0;
      for (let li = 0; li < lines.length; li++) {
        const line = lines[li];
        const ly = textY + li * lineH;
        const lineStart = charIdx;
        const lineEnd = charIdx + line.length;
        const selA = Math.min(this._selStart, this._selEnd);
        const selB = Math.max(this._selStart, this._selEnd);

        if (selB > lineStart && selA < lineEnd) {
          const hlStart = Math.max(0, selA - lineStart);
          const hlEnd = Math.min(line.length, selB - lineStart);
          const xStart = x + padX + ctx.measureText(line.slice(0, hlStart)).width;
          const xEnd = x + padX + ctx.measureText(line.slice(0, hlEnd)).width;
          ctx.fillRect(xStart, ly, xEnd - xStart, lineH);
        }
        charIdx = lineEnd + 1; // +1 for newline-equivalent wrap break
      }
    }

    // Draw text lines
    ctx.fillStyle = 'rgba(50, 35, 15, 0.88)';
    for (let li = 0; li < lines.length; li++) {
      ctx.fillText(lines[li], x + padX, textY + li * lineH);
    }

    // Draw cursor
    if (this._blinkOn) {
      let charIdx = 0;
      for (let li = 0; li < lines.length; li++) {
        const line = lines[li];
        const lineStart = charIdx;
        const lineEnd = charIdx + line.length;

        if (this._cursor >= lineStart && this._cursor <= lineEnd) {
          const cursorOffset = this._cursor - lineStart;
          const cursorX = x + padX + ctx.measureText(line.slice(0, cursorOffset)).width;
          const cursorY = textY + li * lineH;
          ctx.strokeStyle = 'rgba(50, 35, 15, 0.7)';
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(cursorX, cursorY);
          ctx.lineTo(cursorX, cursorY + lineH);
          ctx.stroke();
          break;
        }
        charIdx = lineEnd + 1;
      }
    }

    // Auto-scroll: ensure cursor line is visible
    let charIdx2 = 0;
    for (let li = 0; li < lines.length; li++) {
      const lineEnd = charIdx2 + lines[li].length;
      if (this._cursor >= charIdx2 && this._cursor <= lineEnd) {
        const cursorLineY = li * lineH;
        if (cursorLineY - this._scrollY < 0) {
          this._scrollY = cursorLineY;
        } else if (cursorLineY + lineH - this._scrollY > h - padY * 2) {
          this._scrollY = cursorLineY + lineH - (h - padY * 2);
        }
        break;
      }
      charIdx2 = lineEnd + 1;
    }

    ctx.restore();
  }

  // ── Text wrapping ──────────────────────────────────────────────────────

  private _wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const result: string[] = [];
    const paragraphs = text.split('\n');
    for (const para of paragraphs) {
      if (para.length === 0) { result.push(''); continue; }
      const words = para.split(' ');
      let line = '';
      for (const word of words) {
        const test = line ? line + ' ' + word : word;
        if (ctx.measureText(test).width > maxWidth && line) {
          result.push(line);
          line = word;
        } else {
          line = test;
        }
      }
      result.push(line);
    }
    return result;
  }
}
